import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchApi } from '../api';

const TYPE_META = {
  vertrag: { icon: '📋', label: 'Vertrag', color: '#818cf8' },
  dokument: { icon: '📄', label: 'Dokument', color: '#38bdf8' },
  zahlung: { icon: '💳', label: 'Zahlung', color: '#34d399' },
  ausgabe: { icon: '💰', label: 'Ausgabe', color: '#fbbf24' },
};

export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSuggestions([]);
      setError('');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    const timer = setTimeout(async () => {
      try {
        const [res, suggestionRes] = await Promise.all([
          searchApi.global(trimmed, { limit: 8 }),
          searchApi.suggestions(trimmed),
        ]);
        if (!cancelled) {
          setResults(res.data.flatResults || []);
          setSuggestions(suggestionRes.data.autocomplete || []);
          setOpen(true);
        }
      } catch {
        if (!cancelled) {
          setResults([]);
          setError('Suche nicht verfügbar');
          setOpen(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 280);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const goToResult = (result) => {
    if (!result?.url) return;
    setOpen(false);
    setQuery('');
    navigate(result.url);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
    if (event.key === 'Enter' && results.length > 0) {
      event.preventDefault();
      goToResult(results[0]);
    }
  };

  const shouldShowPanel = open && query.trim().length >= 2;

  return (
    <div ref={wrapperRef} style={{ position: 'relative', flex: '1 1 320px', maxWidth: 460 }}>
      <input
        ref={inputRef}
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => query.trim().length >= 2 && setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="Suchen..."
        aria-label="Globale Suche"
        style={{
          width: '100%',
          background: '#0f172a',
          border: '1px solid #334155',
          color: '#f1f5f9',
          padding: '8px 12px',
          borderRadius: 8,
          fontSize: 13,
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />

      {shouldShowPanel && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          left: 0,
          right: 0,
          background: '#111827',
          border: '1px solid #334155',
          borderRadius: 8,
          boxShadow: '0 18px 40px rgba(0,0,0,0.35)',
          overflow: 'hidden',
          zIndex: 100,
        }}>
          {loading ? (
            <PanelState text="Suche läuft..." />
          ) : error ? (
            <PanelState text={error} color="#f87171" />
          ) : results.length === 0 ? (
            <PanelState text="Keine Treffer" />
          ) : (
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {suggestions.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: 10, borderBottom: '1px solid #1f2937' }}>
                  {suggestions.slice(0, 5).map(item => (
                    <button key={item.term} type="button" onClick={() => setQuery(item.term)} style={suggestionPill}>{item.term}</button>
                  ))}
                </div>
              )}
              {results.map((result, index) => (
                <ResultRow
                  key={`${result.type}-${result.id}-${index}`}
                  result={result}
                  onClick={() => goToResult(result)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PanelState({ text, color = '#94a3b8' }) {
  return (
    <div style={{ padding: '14px 16px', color, fontSize: 13 }}>
      {text}
    </div>
  );
}

function ResultRow({ result, onClick }) {
  const meta = TYPE_META[result.type] || { icon: '🔎', label: result.type || 'Treffer', color: '#94a3b8' };
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '10px 12px',
        border: 0,
        borderBottom: '1px solid #1f2937',
        background: 'transparent',
        color: '#f1f5f9',
        textAlign: 'left',
        cursor: 'pointer',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#1e293b'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ width: 24, fontSize: 17, lineHeight: '22px' }}>{meta.icon}</span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}>
          <span style={{ color: meta.color, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{meta.label}</span>
          <span style={{ color: '#64748b', fontSize: 11 }}>{result.matchedField}</span>
          <span style={{ color: '#64748b', fontSize: 11 }}>Score {result.score}</span>
        </span>
        <span style={{ display: 'block', color: '#f8fafc', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {result.title}
        </span>
        {(result.snippet || result.subtitle || result.meta) && (
          <span style={{ display: 'block', color: '#94a3b8', fontSize: 12, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {[result.snippet, result.subtitle, result.meta].filter(Boolean).join(' · ')}
          </span>
        )}
      </span>
    </button>
  );
}

const suggestionPill = {
  background: '#0f172a',
  color: '#cbd5e1',
  border: '1px solid #334155',
  borderRadius: 999,
  padding: '4px 8px',
  fontSize: 11,
  cursor: 'pointer',
};
